// frontend/src/modules/platform-billing/api.ts
import { api, getToken } from '../../core/api-client';
import type {
  ModulePrice, ModulePriceInput,
  TenantSubscription, SubscriptionStatus,
  PlatformInvoice, BillProjection, SeatCounts,
} from './types';

export const billingApi = {
  // Super-admin
  listPrices: () =>
    api<{ prices: ModulePrice[] }>('/api/admin/billing/module-prices').then((r) => r.prices),
  upsertPrice: (input: ModulePriceInput) =>
    api<{ price: ModulePrice }>('/api/admin/billing/module-prices', { method: 'POST', body: input }).then((r) => r.price),
  updatePrice: (id: string, patch: Partial<ModulePriceInput>) =>
    api<{ price: ModulePrice }>(`/api/admin/billing/module-prices/${id}`, { method: 'PATCH', body: patch }).then((r) => r.price),
  deletePrice: (id: string) =>
    api<{ ok: true }>(`/api/admin/billing/module-prices/${id}`, { method: 'DELETE' }),
  listSubscriptions: (status?: SubscriptionStatus) =>
    api<{ subscriptions: TenantSubscription[] }>(`/api/admin/billing/subscriptions${status ? `?status=${status}` : ''}`).then((r) => r.subscriptions),
  cancelSubscription: (tenantId: string) =>
    api<{ subscription: TenantSubscription }>(`/api/admin/billing/subscriptions/${tenantId}/cancel`, { method: 'POST', body: {} }).then((r) => r.subscription),

  // Tenant
  mySubscription: () =>
    api<{ subscription: TenantSubscription }>('/api/billing/subscription').then((r) => r.subscription),
  myInvoices: () =>
    api<{ invoices: PlatformInvoice[] }>('/api/billing/invoices').then((r) => r.invoices),
  cyclePreview: () =>
    api<{ projection: BillProjection }>('/api/billing/preview').then((r) => r.projection),
  /**
   * Live metered seat counts for the current subscription period (pricing
   * revamp, 2026-07-25). Used by the plan editor / trial wizard to price
   * per-employee lines from real usage instead of a manually-entered seat
   * count.
   *
   * Backend endpoint: `GET /api/billing/seat-counts`
   * Returns: `{ clock_in, monitored, employees }`
   */
  seatCounts: () => api<SeatCounts>('/api/billing/seat-counts'),
  payLink: (invoiceId: string) =>
    api<{ url: string; session_id: string }>(`/api/billing/invoices/${invoiceId}/pay-link`, { method: 'POST', body: {} }),

  /**
   * Self-serve card save: returns a Stripe Checkout (setup mode) URL. Works for
   * any non-cancelled status — the read_only dead-end fix.
   */
  addPaymentMethod: () =>
    api<{ url: string; session_id: string }>('/api/billing/payment-method', { method: 'POST', body: {} }),

  /**
   * Self-serve cancellation of the CALLER'S OWN subscription. Cancelling stops
   * future invoices and clears the card on file server-side; it never deletes
   * the workspace or any data.
   *
   * Backend endpoint: `POST /api/billing/subscription/cancel`
   * Permission: `billing.pay` (the tenant-side billing write permission).
   * Returns: `{ subscription: TenantSubscription }`
   */
  cancelMySubscription: () =>
    api<{ subscription: TenantSubscription }>('/api/billing/subscription/cancel', {
      method: 'POST',
      body: {},
    }).then((r) => r.subscription),

  /** Fetch a platform invoice PDF as an authed blob and trigger a browser download. */
  downloadInvoicePdf: async (invoiceId: string, invoiceNumber: string): Promise<void> => {
    const token = getToken();
    const res = await fetch(`/api/billing/invoices/${invoiceId}/pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${invoiceNumber}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  /**
   * Patch the current tenant's subscription. Used by the trial onboarding wizard
   * and the plan editor to persist module selections as the user advances
   * through the steps.
   *
   * Pricing revamp (2026-07-25): `seatCount` is deprecated and no longer
   * accepted by this function's payload type — the backend silently ignores
   * it if sent, but the UI must not send it. Per-employee charges are always
   * metered live from real usage (see `seatCounts` above), never set by the
   * tenant.
   *
   * Backend endpoint: `PATCH /api/billing/subscription`
   * Body: `{ enabledModuleKeys?: string[] }`
   * Returns: `{ subscription: TenantSubscription }`
   */
  updateMySubscription: (patch: { enabledModuleKeys?: string[] }) =>
    api<{ subscription: TenantSubscription }>('/api/billing/subscription', {
      method: 'PATCH',
      body: patch,
    }).then((r) => r.subscription),

  /**
   * Public module price catalogue used by the trial wizard to render base +
   * per-seat amounts. Reads the same prices the bill engine uses; safe for any
   * authenticated tenant user to read.
   *
   * Backend endpoint assumed: `GET /api/billing/module-prices`
   * Returns: `{ prices: ModulePrice[] }`
   */
  listMyModulePrices: () =>
    api<{ prices: ModulePrice[] }>('/api/billing/module-prices').then((r) => r.prices),
};
